import { forwardRef, useState } from 'react';
import type { InputHTMLAttributes } from 'react';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, helperText, className = '', style, ...props }, ref) => {
    const [focused, setFocused] = useState(false);

    const inputStyle: React.CSSProperties = {
      width: '100%',
      padding: '0.625rem 0.875rem',
      background: '#1a2235',
      border: `1px solid ${error ? '#ef4444' : focused ? '#3b82f6' : '#1e2d45'}`,
      borderRadius: 8,
      color: '#f1f5f9',
      fontSize: '0.9rem',
      fontFamily: 'inherit',
      outline: 'none',
      transition: 'border-color 0.18s, box-shadow 0.18s',
      boxShadow: error
        ? '0 0 0 3px rgba(239,68,68,0.12)'
        : focused
          ? '0 0 0 3px rgba(59,130,246,0.18)'
          : 'none',
      WebkitAppearance: 'none',
      ...style,
    };

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
        {label && (
          <label
            style={{
              fontSize: '0.8rem',
              fontWeight: 600,
              color: '#64748b',
              letterSpacing: '0.02em',
              display: 'flex',
              alignItems: 'center',
              gap: 3,
            }}
          >
            {label}
            {props.required && <span style={{ color: '#ef4444', fontSize: '0.75rem' }}>*</span>}
          </label>
        )}
        <input
          ref={ref}
          className={className}
          style={inputStyle}
          onFocus={e => { setFocused(true); props.onFocus?.(e); }}
          onBlur={e => { setFocused(false); props.onBlur?.(e); }}
          {...props}
        />
        {error && (
          <p role="alert" style={{ fontSize: '0.78rem', color: '#f87171', display: 'flex', alignItems: 'center', gap: 4 }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 8v4m0 4h.01" strokeLinecap="round" />
            </svg>
            {error}
          </p>
        )}
        {helperText && !error && (
          <p style={{ fontSize: '0.75rem', color: '#475569', lineHeight: 1.5 }}>{helperText}</p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
